import { cn } from "@universe/ui/lib/utils";
import { format } from "date-fns";
import { useTranslations } from "next-intl";

import {
  LEDGER_ROW_BOX_CLASSES,
  LEDGER_ROW_DIVIDER_CLASSES,
  LEDGER_ROW_LAYOUT_CLASSES,
  LEDGER_ROW_SEPARATOR_CLASSES,
  LEDGER_ROW_SHELL_CLASSES,
} from "@/components/ledger/ledger-row-shell";
import { ProcessingItem } from "@/components/payments/processing-payments-list";
import { UnconfirmedItem } from "@/components/payments/unconfirmed-payments-list";
import { LEDGER_ENTRY_DATE_TIME_SHORT_FORMAT } from "@/constants";
import {
  getBeneficiaryDisplayName,
  getLegalEntityName,
} from "@/utils/getLegalEntityName";

import CurrencyFlag from "../currency-flag";
import FormattedCurrencyAmount from "../formatted-currency-amount";
import InstructionStatusBadge from "../ledger/instruction-status-badge";
import LedgerDirectionIcon from "../ledger/ledger-direction-icon";
import { JournalType } from "../ledger/types";
import { NoTranslate } from "../no-translate";

interface PaymentItemProps {
  index: number;
  payment: UnconfirmedItem | ProcessingItem;
  setSelectedPayment: () => void;
  selectedIndex: string | null;
  showStatusBadge?: boolean;
}

export default function PaymentItem({
  index,
  payment,
  setSelectedPayment,
  selectedIndex,
  showStatusBadge = false,
}: PaymentItemProps) {
  const tLedgerReasons = useTranslations("ledger.reasons");
  const tLedgerFallback = useTranslations("ledger.fallback");

  if (!payment) return null;

  const isPayment = payment._type === "payment";

  // Get currency based on type
  const currency = isPayment
    ? payment.currency
    : payment.beneficiary?.asset?.ledgerProduct;

  // Get beneficiary name based on type; when the BFF failed to resolve the
  // beneficiary (empty object), fall back to the ledger-style "Sent {product}"
  const beneficiaryName =
    (isPayment
      ? getLegalEntityName(payment.beneficiary)
      : getBeneficiaryDisplayName(payment.beneficiary)) ||
    tLedgerFallback("sent", { product: currency || "" });

  // Get amount
  const amount = payment.amount ?? 0;

  // Get date based on type
  const date = isPayment
    ? payment.meta?.createTime || payment.paymentDate || ""
    : payment.meta?.createTime || payment.withdrawalDate || "";

  // Format date and time
  const displayDate = date
    ? format(new Date(date), LEDGER_ENTRY_DATE_TIME_SHORT_FORMAT)
    : "";

  // Get journal type
  const journalType = isPayment
    ? JournalType.PAYMENTS
    : JournalType.BLOCKCHAIN_WITHDRAWALS;

  // Get remittanceInformation (only for payments, not blockchain withdrawals)
  const remittanceInformation = isPayment
    ? payment.remittanceInformation
    : undefined;

  const isSelected = selectedIndex === `${payment.id}-${index}`;

  return (
    <div
      className={`transition-colors hover:bg-gradient-to-l hover:from-white hover:to-gray-100 hover:shadow-md transition-all ${LEDGER_ROW_SHELL_CLASSES}`}
    >
      {/* The row opens the payment drawer, so it is a button named by its
          visible name, amount and date; its width matches the block box it
          replaced (`ledger/ledger-entry-item.tsx`). */}
      <button
        type="button"
        onClick={setSelectedPayment}
        aria-haspopup="dialog"
        aria-expanded={isSelected}
        className={cn(
          LEDGER_ROW_BOX_CLASSES,
          "block w-[calc(100%+24px)] text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-boundary",
          isSelected && "bg-brand-primary/10 shadow-md"
        )}
      >
        {/* Below md the row wraps: the info side takes the full width, so a
            long beneficiary name keeps it, and the amount and date move onto
            their own line. A capped name beside the amount kept only ~6
            characters at 320px. No viewport cap holds the name at any width:
            at 1024px one cut a 40-character name to 36 with 239px of the row
            free. `contain:inline-size` keeps the nowrap name out of the row's
            intrinsic width instead. From md up it sits on the info side, which
            then fills the row: without it the full name widens the sidebar
            inset past the screen from lg. Below md it sits on the name column:
            the app's content column clips the row there, but a shrink-to-fit
            ancestor, such as a story's root, would still widen.
            The name shrinks to an ellipsis beside the amount only if every box
            between the info side and the `<p>` has `min-w-0`: from md the name
            column, which sizes to the name, and from xl also the two wrappers
            inside it, where the column lays the name, remittance, subtitle and
            badge out in one line. Without them the name keeps its full width,
            so the info side's `overflow-hidden` cuts it with no ellipsis, and
            from xl it also pushes the subtitle and badge out of the row. */}
        <div className={cn(LEDGER_ROW_LAYOUT_CLASSES, "items-start")}>
          {/* Left side - Basic info */}
          <div className="flex items-center gap-1.5 overflow-hidden min-w-0 basis-full md:flex-1 md:[contain:inline-size]">
            {/* Mobile stacked layout (< md) */}
            <div className="relative mr-1.5 md:hidden">
              <CurrencyFlag size="28px" currency={currency || ""} />
              <div className="absolute -bottom-1 -right-2 rounded-full bg-[radial-gradient(circle,white_0%,oklch(from_var(--brand-primary)_l_c_h_/_0.15)_100%)]">
                <LedgerDirectionIcon
                  journalType={journalType}
                  balance={-amount}
                  isFee={false}
                  className="w-5 h-5 translate-x-0 scale-100"
                />
              </div>
            </div>

            {/* Desktop side-by-side layout (md+) */}
            <div className="hidden md:contents">
              <LedgerDirectionIcon
                journalType={journalType}
                balance={-amount}
                isFee={false}
              />
              <div className="mr-1.5">
                <CurrencyFlag size="24px" currency={currency || ""} />
              </div>
            </div>

            {/* Beneficiary name and status */}
            {/* Desktop: Title (remittanceInformation) • Payment (Status badge) */}
            {/* Mobile: Title on line 1, remittanceInformation • Payment on line 2, Status badge on line 3 */}
            <div className="flex flex-col xl:flex-row xl:gap-2 min-w-0 flex-1 [contain:inline-size] md:flex-initial md:[contain:none]">
              <div className="flex flex-col xl:flex-row xl:items-center gap-0 xl:gap-2 min-w-0">
                {/* Title */}
                <div className="flex items-center min-w-0">
                  <p className="font-medium text-base md:text-lg -ml-[2px] xl:ml-0 truncate">
                    <NoTranslate>{beneficiaryName}</NoTranslate>
                  </p>
                </div>

                {/* Desktop: (remittanceInformation) • */}
                {remittanceInformation && (
                  <>
                    <span className="text-sm md:text-sm text-gray-700 hidden xl:inline">
                      ({remittanceInformation})
                    </span>
                    <span className="text-gray-800 text-xl lg:text-2xl hidden xl:block">
                      •
                    </span>
                  </>
                )}

                {/* Desktop: • (when no remittanceInformation) */}
                {!remittanceInformation && (
                  <span className="text-gray-800 text-xl lg:text-2xl hidden xl:block">
                    •
                  </span>
                )}

                {/* Transaction type and status badge section */}
                <div className="flex flex-col xl:flex-row xl:items-center gap-1">
                  {/* Mobile: remittanceInformation • Payment */}
                  <div className="flex flex-row items-center gap-1.5 xl:gap-1">
                    {remittanceInformation && (
                      <>
                        <span className="text-sm md:text-sm text-gray-700 xl:hidden">
                          {remittanceInformation}
                        </span>
                        <span className="text-gray-800 text-xl xl:hidden">
                          •
                        </span>
                      </>
                    )}
                    <span className="text-sm md:text-sm text-gray-700">
                      {tLedgerReasons("debit")}
                    </span>
                  </div>
                  {/* Status badge - on own line on mobile, inline on desktop */}
                  {showStatusBadge && payment.instructionState && (
                    <div className="w-fit xl:contents">
                      <InstructionStatusBadge
                        status={payment.instructionState}
                        className="-ml-1 xl:ml-1"
                        size="xs"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Amount and date */}
          <div className="text-right flex items-center gap-10 ml-auto">
            <div className="text-right flex flex-row items-baseline gap-3 md:flex-col md:items-end md:gap-1 xl:flex-row">
              <FormattedCurrencyAmount
                balance={-amount}
                product={currency || ""}
              />

              <div className={LEDGER_ROW_SEPARATOR_CLASSES} />

              <p className="text-sm xl:text-base text-gray-700 w-[80px]">
                {displayDate}
              </p>
            </div>
          </div>
        </div>
      </button>
      <div className={LEDGER_ROW_DIVIDER_CLASSES} />
    </div>
  );
}
